import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Modal, Select, Tooltip, Form, Drawer, Divider, Descriptions } from 'antd';
import { MapPin, Clock, Edit, Eye } from 'lucide-react';
import { get_employee_user_list, add_employee_user, get_shift, get_warehouse_areas_dropdown } from '@/utils/api';
import { toast } from 'react-toastify';
import ReusableSearchInput from '@/components/ReusableSearch';
import { useTheme } from '@/App';

const { Option } = Select;

const UserAccessManagement = () => {
  const { isDark } = useTheme();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [shifts, setShifts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editModal, setEditModal] = useState({ open: false, record: null, submitting: false });
  const [detailRecord, setDetailRecord] = useState(null);
  const [form] = Form.useForm();

  const fetchEmployees = () => {
    setLoading(true);
    get_employee_user_list((res) => {
      setLoading(false);
      if (res?.status === 'success') setEmployees(res.data || []);
    });
  };

  useEffect(() => {
    fetchEmployees();
    get_shift((res) => setShifts(res?.data?.data || []));
    get_warehouse_areas_dropdown((res) => setLocations(res?.data?.data || []));
  }, []);

  const openEdit = (record) => {
    form.setFieldsValue({
      shift_id: record.shifts?.length ? record.shifts : (record.shift_id ? [record.shift_id] : []),
      location_id: record.locations?.length ? record.locations : (record.location_id ? [record.location_id] : []),
    });
    setEditModal({ open: true, record, submitting: false });
  };

  const closeModal = () => setEditModal({ open: false, record: null, submitting: false });

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setEditModal((prev) => ({ ...prev, submitting: true }));
      const result = await add_employee_user(
        {
          id: editModal.record.employee_id,
          shift_id: values.shift_id,
          location_id: values.location_id,
        },
        (res) => {
          closeModal();
          if (res?.status === 'success') {
            toast.success('Updated successfully');
            fetchEmployees();
          } else {
            toast.error(res?.message || 'Failed to update');
          }
        }
      );
      // handleResponse only calls the callback on 2xx; close for non-2xx responses
      if (result && !result.ok) closeModal();
    } catch (e) {
      closeModal();
    }
  };

  const filteredData = employees.filter((emp) => {
    if (!searchValue) return true;
    const q = searchValue.toLowerCase();
    return (
      emp.employee_name?.toLowerCase().includes(q) ||
      emp.department?.toLowerCase().includes(q) ||
      emp.location?.toLowerCase().includes(q) ||
      emp.username?.toLowerCase().includes(q)
    );
  });

  const parseRoleNames = (role_name) => {
    try {
      if (Array.isArray(role_name)) return role_name;
      return JSON.parse(role_name.replace(/'/g, '"'));
    } catch {
      return [role_name];
    }
  };

  const columns = [
    {
      title: '#',
      key: 'idx',
      width: 50,
      align: 'center',
      render: (_, __, idx) => idx + 1,
    },
    {
      title: 'User ID',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (t) => <span style={{ fontWeight: 500 }}>{t || '—'}</span>,
    },
    {
      title: 'Name',
      dataIndex: 'employee_name',
      key: 'employee_name',
      width: 170,
      render: (t) => <span style={{ fontWeight: 500 }}>{t || '—'}</span>,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 150,
      render: (t) => t ? <Tag color="blue">{t}</Tag> : '—',
    },
    {
      title: 'Designation',
      dataIndex: 'designation',
      key: 'designation',
      width: 150,
      render: (t) => t ? <Tag color="geekblue">{t}</Tag> : '—',
    },
    {
      title: 'Roles',
      dataIndex: 'role_name',
      key: 'role_name',
      width: 220,
      render: (role_name) => {
        const names = parseRoleNames(role_name || '[]');
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {names.map((r, i) => <Tag key={i} color="purple">{r}</Tag>)}
          </div>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <div className="flex justify-center" style={{ gap: 2 }}>
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              className="flex items-center justify-center text-teal-600 hover:text-teal-800 hover:bg-teal-50"
              icon={<Eye size={20} />}
              onClick={() => setDetailRecord(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Shift & Location">
            <Button
              type="text"
              size="small"
              className="flex items-center justify-center text-blue-600 hover:text-blue-800 hover:bg-blue-50"
              icon={<Edit size={20} />}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <ReusableSearchInput
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onClear={() => setSearchValue('')}
          placeholder="Search by name, department, location..."
          style={{ width: 300 }}
        />
      </div>

      <Table
        rowKey="employee_id"
        dataSource={filteredData}
        columns={columns}
        loading={loading}
        bordered
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}` }}
      />

      {/* Edit Modal — only shift & location editable */}
      <Modal
        open={editModal.open}
        onCancel={() => setEditModal({ open: false, record: null, submitting: false })}
        onOk={handleSave}
        okText="Save"
        cancelText="Cancel"
        confirmLoading={editModal.submitting}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Edit size={16} style={{ color: '#1890ff' }} />
            <span>Edit Access — {editModal.record?.employee_name}</span>
          </div>
        }
        width={480}
        centered
        destroyOnClose
      >
        {/* Read-only info */}
        <div style={{ background: isDark ? '#1f2937' : '#f9fafb', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: '#888', width: 100 }}>User ID:</span>
            <span style={{ fontWeight: 500 }}>{editModal.record?.username}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: '#888', width: 100 }}>Name:</span>
            <span style={{ fontWeight: 500 }}>{editModal.record?.employee_name}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: '#888', width: 100 }}>Department:</span>
            <Tag color="blue">{editModal.record?.department || '—'}</Tag>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ color: '#888', width: 100 }}>Roles:</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {parseRoleNames(editModal.record?.role_name || '[]').map((r, i) => (
                <Tag key={i} color="purple">{r}</Tag>
              ))}
            </div>
          </div>
        </div>

        <Form form={form} layout="vertical">
          <Form.Item
            label={<span><Clock size={14} style={{ marginRight: 5, verticalAlign: 'middle', color: '#1890ff' }} />Shift</span>}
            name="shift_id"
          >
            <Select mode="multiple" placeholder="Select Shift" allowClear showSearch style={{ width: '100%' }}
              optionFilterProp="children"
              filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
            >
              {shifts.map((s) => (
                <Option key={s.shift_id} value={s.shift_id}>{s.shift}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={<span><MapPin size={14} style={{ marginRight: 5, verticalAlign: 'middle', color: '#52c41a' }} />Location (Warehouse Area)</span>}
            name="location_id"
          >
            <Select mode="multiple" placeholder="Select Location" allowClear showSearch style={{ width: '100%' }}
              optionFilterProp="children"
              filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
            >
              {locations.map((l) => (
                <Option key={l.warehouse_area_id || l.location_id || l.id} value={l.warehouse_area_id || l.location_id || l.id}>
                  {l.area_name || l.location_name || l.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Details Drawer */}
      <Drawer
        title={<span style={{ fontWeight: 700 }}>Details — {detailRecord?.employee_name}</span>}
        placement="right"
        width={400}
        open={!!detailRecord}
        onClose={() => setDetailRecord(null)}
        mask={false}
        zIndex={2001}
      >
        {detailRecord && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Clock size={13} /> Shifts
              </div>
              {detailRecord.shifts_names?.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {detailRecord.shifts_names.map((name, i) => (
                    <Tag key={i} color="blue" style={{ margin: 0 }}>{name}</Tag>
                  ))}
                </div>
              ) : detailRecord.shift && detailRecord.shift !== 'None-None' ? (
                <Tag color="blue">{detailRecord.shift}</Tag>
              ) : (
                <span style={{ fontSize: 13, color: '#9ca3af' }}>No shifts assigned</span>
              )}
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <MapPin size={13} /> Locations
              </div>
              {detailRecord.locations_names?.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {detailRecord.locations_names.map((name, i) => (
                    <Tag key={i} color="green" style={{ margin: 0 }}>{name}</Tag>
                  ))}
                </div>
              ) : detailRecord.location ? (
                <Tag color="green">{detailRecord.location}</Tag>
              ) : (
                <span style={{ fontSize: 13, color: '#9ca3af' }}>No locations assigned</span>
              )}
            </div>

            <Divider style={{ margin: '4px 0' }} />

            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Username">{detailRecord.username}</Descriptions.Item>
              <Descriptions.Item label="Email">{detailRecord.email}</Descriptions.Item>
              <Descriptions.Item label="Contact">{detailRecord.contact}</Descriptions.Item>
              <Descriptions.Item label="Department">{detailRecord.department}</Descriptions.Item>
              <Descriptions.Item label="Designation">{detailRecord.designation}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default UserAccessManagement;
